/**
 * AdminSyncPage - Admin Sync Control Plane UI
 *
 * Phase 1+2+3 features:
 * - Start sync runs (ids or cohort mode)
 * - Step toggles (tmdb, omdb, meili, kv_warm)
 * - View recent sync runs with pagination
 * - Navigate to run detail for progress/items/summary
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  Play,
  Database,
  Search,
  Layers,
  HardDrive,
} from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { useAuth } from '../../contexts/AuthContext';
import { apiFetch } from '../../lib/api';

// ============================================
// Types
// ============================================

interface SyncJobRun {
  id: string;
  job_type: string;
  selector: {
    ids?: Record<string, number[]>;
    cohort?: { type: string; cursor?: unknown };
  };
  media_types: string[];
  steps: {
    tmdb?: { enabled: boolean };
    omdb?: { enabled: boolean };
    meili?: { enabled: boolean };
    kv_warm?: { enabled: boolean };
  };
  max_items: number;
  dry_run: boolean;
  items_selected: number;
  items_queued: number;
  items_processing: number;
  items_succeeded: number;
  items_failed: number;
  items_skipped: number;
  created_count: number;
  updated_count: number;
  unchanged_count: number;
  started_at: string;
  finished_at: string | null;
  operator_user_id: string | null;
}

interface SyncJobsResponse {
  jobs: SyncJobRun[];
  next_cursor: string | null;
}

interface CohortParams {
  from?: string;
  to?: string;
  media_types?: string[];
  days_old?: number;
}

interface StartSyncRunRequest {
  dry_run: boolean;
  media_types: string[];
  selector: {
    ids?: Record<string, number[]>;
    cohort?: { type: string; cursor?: unknown; params?: CohortParams };
  };
  steps: {
    tmdb?: { enabled: boolean };
    omdb?: { enabled: boolean };
    meili?: { enabled: boolean };
    kv_warm?: { enabled: boolean };
  };
  max_items: number;
}

interface StartSyncRunResponse {
  job_run_id: string;
  items_selected: number;
  batches_enqueued: number;
  dry_run: boolean;
  next_cursor?: unknown;
  message?: string;
}

// ============================================
// Helpers
// ============================================

type UiStatus = 'running' | 'has_errors' | 'complete' | 'dry_run';

function deriveStatus(job: SyncJobRun): UiStatus {
  if (job.dry_run && job.items_skipped === job.items_selected) return 'dry_run';
  if (!job.finished_at) return 'running';
  if (job.items_failed > 0) return 'has_errors';
  return 'complete';
}

function getStatusIcon(status: UiStatus) {
  switch (status) {
    case 'complete':
      return <CheckCircle2 size={16} className="text-green-400" />;
    case 'has_errors':
      return <XCircle size={16} className="text-red-400" />;
    case 'running':
      return <Play size={16} className="text-blue-400" />;
    case 'dry_run':
      return <Clock size={16} className="text-yellow-400" />;
    default:
      return <Clock size={16} className="text-slate-400" />;
  }
}

function getStatusLabel(status: UiStatus): string {
  switch (status) {
    case 'complete':
      return 'Complete';
    case 'has_errors':
      return 'Has Errors';
    case 'running':
      return 'Running';
    case 'dry_run':
      return 'Dry Run';
    default:
      return 'Unknown';
  }
}

function formatDuration(startedAt: string, finishedAt: string | null): string {
  const start = new Date(startedAt).getTime();
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now();
  const durationMs = end - start;

  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
}

function formatSelectorSummary(job: SyncJobRun): string {
  if (job.selector.cohort) {
    return `Cohort: ${job.selector.cohort.type}`;
  }
  if (job.selector.ids) {
    const counts = Object.entries(job.selector.ids)
      .map(([type, ids]) => `${ids.length} ${type}`)
      .join(', ');
    return `IDs: ${counts}`;
  }
  return 'Unknown';
}

function formatSteps(steps: SyncJobRun['steps']): string {
  const enabled = [];
  if (steps.tmdb?.enabled) enabled.push('TMDB');
  if (steps.omdb?.enabled) enabled.push('OMDB');
  if (steps.meili?.enabled) enabled.push('Meili');
  if (steps.kv_warm?.enabled) enabled.push('KV');
  return enabled.join(', ') || 'None';
}

// Cohort types and their configurations
type CohortParamType = 'date_range' | 'days_old' | 'none';

interface CohortOption {
  value: string;
  label: string;
  mediaTypes: readonly string[];
  needsParams: boolean;
  paramType: CohortParamType;
}

const COHORT_OPTIONS: CohortOption[] = [
  { value: 'persons_missing_bio_v1', label: 'Persons Missing Bio', mediaTypes: ['person'], needsParams: false, paramType: 'none' },
  { value: 'tmdb_changes_range_v1', label: 'TMDB Changes (Date Range)', mediaTypes: ['movie', 'tv', 'person'], needsParams: true, paramType: 'date_range' },
  { value: 'tmdb_full_refresh_v1', label: 'TMDB Full Refresh', mediaTypes: ['movie', 'tv', 'person'], needsParams: false, paramType: 'none' },
  { value: 'omdb_incomplete_v1', label: 'OMDB Incomplete', mediaTypes: ['movie', 'tv'], needsParams: false, paramType: 'none' },
  { value: 'omdb_stale_v1', label: 'OMDB Stale', mediaTypes: ['movie', 'tv'], needsParams: true, paramType: 'days_old' },
  { value: 'omdb_full_refresh_v1', label: 'OMDB Full Refresh', mediaTypes: ['movie', 'tv'], needsParams: false, paramType: 'none' },
];

// Helper to get cohort config
function getCohortConfig(cohortType: string): CohortOption {
  return COHORT_OPTIONS.find(opt => opt.value === cohortType) || COHORT_OPTIONS[0];
}

// ============================================
// Component
// ============================================

export function AdminSyncPage() {
  const { session } = useAuth();

  // Jobs list state
  const [response, setResponse] = useState<SyncJobsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const limit = 20;

  // Start run form state
  const [showStartForm, setShowStartForm] = useState(false);
  const [selectorMode, setSelectorMode] = useState<'ids' | 'cohort'>('ids');
  const [idsInput, setIdsInput] = useState('');
  const [mediaType, setMediaType] = useState<'movie' | 'tv' | 'person'>('movie');
  const [cohortType, setCohortType] = useState('persons_missing_bio_v1');
  const [maxItems, setMaxItems] = useState(100);
  const [dryRun, setDryRun] = useState(true);
  // Note: TMDB step is always enabled (no state needed)
  const [stepOmdb, setStepOmdb] = useState(false);
  const [stepMeili, setStepMeili] = useState(false);
  const [stepKvWarm, setStepKvWarm] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [startResult, setStartResult] = useState<StartSyncRunResponse | null>(null);

  // Cohort-specific params
  const [cohortFromDate, setCohortFromDate] = useState(() => {
    // Default to 30 days ago
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [cohortToDate, setCohortToDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [cohortMediaTypes, setCohortMediaTypes] = useState<string[]>(['movie', 'tv']);
  const [cohortDaysOld, setCohortDaysOld] = useState(90);
  const [cohortCursorInput, setCohortCursorInput] = useState('');
  const [cohortCursorError, setCohortCursorError] = useState<string | null>(null);

  // Get current cohort config
  const currentCohortConfig = getCohortConfig(cohortType);

  const fetchJobs = async (cursor?: string) => {
    if (!session?.access_token) return;

    setLoading(true);
    setError(null);

    try {
      let url = `/admin/sync/jobs?limit=${limit}`;
      if (cursor) {
        url += `&cursor=${cursor}`;
      }

      const data = await apiFetch<SyncJobsResponse>(url, {
        token: session.access_token,
      });
      setResponse(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch sync jobs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchJobs();
  }, [session?.access_token]);

  const handleNextPage = () => {
    if (response?.next_cursor) {
      if (response.jobs.length > 0) {
        setCursorStack((prev) => [...prev, response.jobs[0].id]);
      }
      fetchJobs(response.next_cursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorStack.length > 0) {
      const newStack = [...cursorStack];
      newStack.pop();
      setCursorStack(newStack);
      fetchJobs(newStack.length > 0 ? newStack[newStack.length - 1] : undefined);
    }
  };

  const handleStartRun = async () => {
    if (!session?.access_token) return;

    setStartLoading(true);
    setStartError(null);
    setStartResult(null);

    try {
      // Build selector based on mode
      let selector: StartSyncRunRequest['selector'];
      let media_types: string[];

      if (selectorMode === 'ids') {
        // Parse comma-separated IDs
        const ids = idsInput
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !isNaN(n) && n > 0);

        if (ids.length === 0) {
          throw new Error('Please enter at least one valid ID');
        }

        selector = { ids: { [mediaType]: ids } };
        media_types = [mediaType];
      } else {
        // Cohort mode - build params based on cohort type
        const cohortConfig = getCohortConfig(cohortType);
        let params: CohortParams | undefined;

        if (cohortConfig.needsParams && cohortConfig.paramType === 'date_range') {
          // tmdb_changes_range_v1
          if (!cohortFromDate || !cohortToDate) {
            throw new Error('Please specify from and to dates');
          }
          if (cohortMediaTypes.length === 0) {
            throw new Error('Please select at least one media type');
          }
          params = {
            from: cohortFromDate,
            to: cohortToDate,
            media_types: cohortMediaTypes,
          };
        } else if (cohortConfig.needsParams && cohortConfig.paramType === 'days_old') {
          // omdb_stale_v1
          params = { days_old: cohortDaysOld };
        }

        // Parse cursor if provided
        let cursor: Record<string, unknown> | undefined;
        if (cohortCursorInput.trim()) {
          if (cohortCursorError) {
            throw new Error('Invalid cursor JSON');
          }
          try {
            const parsed = JSON.parse(cohortCursorInput);
            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
              throw new Error('Cursor must be a JSON object');
            }
            cursor = parsed;
          } catch {
            throw new Error('Invalid cursor JSON');
          }
        }

        selector = { cohort: { type: cohortType, params, cursor } };
        // Use cohort-specific media types, or filtered selection for tmdb_changes_range_v1
        media_types = cohortConfig.paramType === 'date_range' ? cohortMediaTypes : [...cohortConfig.mediaTypes];
      }

      const request: StartSyncRunRequest = {
        dry_run: dryRun,
        media_types,
        selector,
        steps: {
          tmdb: { enabled: true }, // Always enabled (required step)
          omdb: { enabled: stepOmdb },
          meili: { enabled: stepMeili },
          kv_warm: { enabled: stepKvWarm },
        },
        max_items: maxItems,
      };

      const data = await apiFetch<StartSyncRunResponse>('/admin/sync/run', {
        method: 'POST',
        token: session.access_token,
        body: request,
      });

      setStartResult(data);
      // Refresh jobs list
      fetchJobs();
    } catch (err) {
      setStartError(err instanceof Error ? err.message : 'Failed to start sync run');
    } finally {
      setStartLoading(false);
    }
  };

  return (
    <AdminLayout title="Sync Control Plane" description="TMDB sync with Phase 3 follow-ups">
      <div className="space-y-6">
        {/* Start Sync Run Section */}
        <div className="bg-[#1A1F26] rounded-lg border border-white/[0.08] p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Start Sync Run</h2>
            <button
              onClick={() => setShowStartForm(!showStartForm)}
              className="text-sm text-teal-400 hover:text-teal-300"
            >
              {showStartForm ? 'Hide' : 'Show Form'}
            </button>
          </div>

          {showStartForm && (
            <div className="space-y-4">
              {/* Selector Mode */}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="radio"
                    checked={selectorMode === 'ids'}
                    onChange={() => setSelectorMode('ids')}
                    className="w-4 h-4"
                  />
                  Explicit IDs
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="radio"
                    checked={selectorMode === 'cohort'}
                    onChange={() => setSelectorMode('cohort')}
                    className="w-4 h-4"
                  />
                  Cohort
                </label>
              </div>

              {/* IDs Mode */}
              {selectorMode === 'ids' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Media Type</label>
                    <select
                      value={mediaType}
                      onChange={(e) => setMediaType(e.target.value as 'movie' | 'tv' | 'person')}
                      className="w-full bg-[#0F1419] border border-white/[0.08] rounded px-3 py-2 text-sm text-white"
                    >
                      <option value="movie">Movie</option>
                      <option value="tv">TV</option>
                      <option value="person">Person</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">TMDB IDs (comma-separated)</label>
                    <input
                      type="text"
                      value={idsInput}
                      onChange={(e) => setIdsInput(e.target.value)}
                      placeholder="550, 551, 552"
                      className="w-full bg-[#0F1419] border border-white/[0.08] rounded px-3 py-2 text-sm text-white"
                    />
                  </div>
                </div>
              )}

              {/* Cohort Mode */}
              {selectorMode === 'cohort' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Cohort Type</label>
                    <select
                      value={cohortType}
                      onChange={(e) => setCohortType(e.target.value)}
                      className="w-full bg-[#0F1419] border border-white/[0.08] rounded px-3 py-2 text-sm text-white"
                    >
                      {COHORT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      Media types: {currentCohortConfig.mediaTypes.join(', ')}
                    </p>
                  </div>

                  {/* Date Range params (tmdb_changes_range_v1) */}
                  {currentCohortConfig.paramType === 'date_range' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm text-slate-400 mb-1">From Date</label>
                          <input
                            type="date"
                            value={cohortFromDate}
                            onChange={(e) => setCohortFromDate(e.target.value)}
                            className="w-full bg-[#0F1419] border border-white/[0.08] rounded px-3 py-2 text-sm text-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-slate-400 mb-1">To Date</label>
                          <input
                            type="date"
                            value={cohortToDate}
                            onChange={(e) => setCohortToDate(e.target.value)}
                            className="w-full bg-[#0F1419] border border-white/[0.08] rounded px-3 py-2 text-sm text-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-slate-400 mb-2">Media Types to Sync</label>
                        <div className="flex gap-4">
                          {['movie', 'tv', 'person'].map((mt) => (
                            <label key={mt} className="flex items-center gap-2 text-sm text-slate-300">
                              <input
                                type="checkbox"
                                checked={cohortMediaTypes.includes(mt)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCohortMediaTypes((prev) => [...prev, mt]);
                                  } else {
                                    setCohortMediaTypes((prev) => prev.filter((t) => t !== mt));
                                  }
                                }}
                                className="w-4 h-4"
                              />
                              {mt}
                            </label>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Days Old param (omdb_stale_v1) */}
                  {currentCohortConfig.paramType === 'days_old' && (
                    <div>
                      <label className="block text-sm text-slate-400 mb-1">Days Old (1-365)</label>
                      <input
                        type="number"
                        value={cohortDaysOld}
                        onChange={(e) => setCohortDaysOld(Math.max(1, Math.min(365, parseInt(e.target.value) || 90)))}
                        min={1}
                        max={365}
                        className="w-32 bg-[#0F1419] border border-white/[0.08] rounded px-3 py-2 text-sm text-white"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Sync items where OMDB data is older than this many days
                      </p>
                    </div>
                  )}

                  {/* Resume Cursor (optional) */}
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Resume Cursor (optional)</label>
                    <textarea
                      value={cohortCursorInput}
                      onChange={(e) => {
                        setCohortCursorInput(e.target.value);
                        setCohortCursorError(null);
                        // Validate JSON if non-empty
                        if (e.target.value.trim()) {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
                              setCohortCursorError('Cursor must be a JSON object');
                            }
                          } catch {
                            setCohortCursorError('Invalid JSON');
                          }
                        }
                      }}
                      placeholder='Paste next_cursor from previous run, e.g. {"date":"2025-10-15","page":1}'
                      rows={2}
                      className={`w-full bg-[#0F1419] border rounded px-3 py-2 text-sm text-white font-mono ${
                        cohortCursorError ? 'border-red-500/50' : 'border-white/[0.08]'
                      }`}
                    />
                    {cohortCursorError && (
                      <p className="text-xs text-red-400 mt-1">{cohortCursorError}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-1">
                      To resume a previous run, paste the next_cursor value from the job detail page
                    </p>
                  </div>
                </div>
              )}

              {/* Max Items */}
              <div>
                <label className="block text-sm text-slate-400 mb-1">Max Items (1-1000)</label>
                <input
                  type="number"
                  value={maxItems}
                  onChange={(e) => setMaxItems(Math.max(1, Math.min(1000, parseInt(e.target.value) || 1)))}
                  min={1}
                  max={1000}
                  className="w-32 bg-[#0F1419] border border-white/[0.08] rounded px-3 py-2 text-sm text-white"
                />
              </div>

              {/* Steps */}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Steps</label>
                <div className="flex flex-wrap gap-4">
                  {/* TMDB is always enabled - show as static badge, not a toggle */}
                  <span className="flex items-center gap-2 text-sm text-slate-300 bg-blue-500/10 border border-blue-500/30 rounded px-2 py-1">
                    <Database size={14} className="text-blue-400" />
                    TMDB Sync (always on)
                  </span>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={stepOmdb}
                      onChange={(e) => setStepOmdb(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <Layers size={14} className="text-yellow-400" />
                    OMDB Enrichment
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={stepMeili}
                      onChange={(e) => setStepMeili(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <Search size={14} className="text-purple-400" />
                    Meilisearch Reindex
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={stepKvWarm}
                      onChange={(e) => setStepKvWarm(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <HardDrive size={14} className="text-green-400" />
                    KV Cache Warm
                  </label>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  TMDB sync runs first; follow-up steps run for succeeded items only (not in dry run).
                </p>
              </div>

              {/* Dry Run */}
              <div>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(e) => setDryRun(e.target.checked)}
                    className="w-4 h-4"
                  />
                  Dry Run (preview only, no actual sync)
                </label>
              </div>

              {/* Submit */}
              <div className="flex items-center gap-4">
                <button
                  onClick={handleStartRun}
                  disabled={startLoading}
                  className="px-4 py-2 bg-teal-500 text-white rounded hover:bg-teal-600 disabled:opacity-50 text-sm font-medium"
                >
                  {startLoading ? 'Starting...' : dryRun ? 'Preview Run' : 'Start Sync Run'}
                </button>

                {startError && (
                  <div className="flex items-center gap-2 text-red-400 text-sm">
                    <AlertTriangle size={14} />
                    {startError}
                  </div>
                )}
              </div>

              {/* Result */}
              {startResult && (
                <div className="bg-[#0F1419] rounded p-3 text-sm">
                  <div className="text-green-400 font-medium mb-2">
                    {startResult.dry_run ? 'Dry run completed' : 'Sync run started'}
                  </div>
                  <div className="text-slate-300 space-y-1">
                    <div>Job ID: <Link to={`/admin/sync/${startResult.job_run_id}`} className="text-teal-400 hover:underline">{startResult.job_run_id}</Link></div>
                    <div>Items selected: {startResult.items_selected}</div>
                    {!startResult.dry_run && <div>Batches enqueued: {startResult.batches_enqueued}</div>}
                    {startResult.message && <div className="text-yellow-400">{startResult.message}</div>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Jobs List */}
        <div className="bg-[#1A1F26] rounded-lg border border-white/[0.08]">
          <div className="flex items-center justify-between p-4 border-b border-white/[0.08]">
            <h2 className="text-lg font-semibold text-white">Recent Sync Runs</h2>
            <button
              onClick={() => fetchJobs()}
              disabled={loading}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {error && (
            <div className="p-4 bg-red-500/10 border-b border-red-500/20">
              <div className="flex items-center gap-2 text-red-400 text-sm">
                <AlertTriangle size={14} />
                {error}
              </div>
            </div>
          )}

          {loading && !response && (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          )}

          {response && response.jobs.length === 0 && (
            <div className="p-8 text-center text-slate-400">No sync runs found</div>
          )}

          {response && response.jobs.length > 0 && (
            <>
              <div className="divide-y divide-white/[0.06]">
                {response.jobs.map((job) => {
                  const status = deriveStatus(job);
                  const progressPct = job.items_selected > 0
                    ? Math.round(((job.items_succeeded + job.items_failed + job.items_skipped) / job.items_selected) * 100)
                    : 0;

                  return (
                    <Link
                      key={job.id}
                      to={`/admin/sync/${job.id}`}
                      className="block p-4 hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1.5">
                            {getStatusIcon(status)}
                            <span className="text-xs text-slate-400">{getStatusLabel(status)}</span>
                          </div>
                          <div>
                            <div className="text-sm text-white font-medium">
                              {formatSelectorSummary(job)}
                            </div>
                            <div className="text-xs text-slate-400">
                              {new Date(job.started_at).toLocaleString()} | {formatDuration(job.started_at, job.finished_at)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <div className="text-sm text-white">
                              {job.items_succeeded}/{job.items_selected} succeeded
                            </div>
                            <div className="text-xs text-slate-400">
                              Steps: {formatSteps(job.steps)}
                            </div>
                          </div>
                          <div className="w-20">
                            <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all ${
                                  status === 'has_errors' ? 'bg-red-400' :
                                  status === 'complete' ? 'bg-green-400' :
                                  status === 'dry_run' ? 'bg-yellow-400' :
                                  'bg-blue-400'
                                }`}
                                style={{ width: `${progressPct}%` }}
                              />
                            </div>
                            <div className="text-xs text-slate-400 text-right mt-0.5">
                              {progressPct}%
                            </div>
                          </div>
                          <ChevronRight size={16} className="text-slate-500" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-white/[0.08]">
                <button
                  onClick={handlePrevPage}
                  disabled={cursorStack.length === 0}
                  className="text-sm text-slate-400 hover:text-white disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!response.next_cursor}
                  className="text-sm text-slate-400 hover:text-white disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
